import React, { useState } from 'react';
import type { WalletDTO } from '@push.io/shared';
import type { MenuLanguage } from './types.js';
import { WalletDisplay } from '../customization/WalletDisplay.js';

const LANGUAGES = {
  es: { code: 'ES', flag: '/assets/flags/es.png', fallback: '/assets/flags/es.svg', label: 'Español' },
  'en-US': { code: 'US', flag: '/assets/flags/us.png', fallback: '/assets/flags/us.svg', label: 'English (US)' },
  'en-GB': { code: 'UK', flag: '/assets/flags/gb.png', fallback: '/assets/flags/gb.svg', label: 'English (UK)' },
  fr: { code: 'FR', flag: '/assets/flags/fr.png', fallback: '/assets/flags/fr.svg', label: 'Français' },
} as const;

export function Header({ language, wallet, onSettings, onLanguage, accountButton }: {
  language: MenuLanguage;
  wallet: WalletDTO | null;
  onSettings: () => void;
  onLanguage: (language: MenuLanguage) => void;
  accountButton?: React.ReactNode;
}) {
  const [languageOpen, setLanguageOpen] = useState(false);
  const es = language === 'es';
  const fr = language === 'fr';
  const current = LANGUAGES[language];

  return (
    <header className="main-menu-header">
      <div className="menu-brand">
        <strong>PUSH.IO</strong>
        <span>{es ? 'Empuja. Sobrevive. Gana.' : fr ? 'Poussez. Survivez. Gagnez.' : 'Push. Survive. Win.'}</span>
      </div>
      <nav className="menu-header-actions" aria-label={es ? 'Opciones principales' : fr ? 'Options principales' : 'Main options'}>
        <WalletDisplay wallet={wallet} language={language} />
        <div className="header-language">
          <button className="language-trigger"
            aria-label={es ? 'Cambiar idioma' : fr ? 'Changer de langue' : 'Change language'}
            aria-expanded={languageOpen}
            title={es ? 'Idioma' : fr ? 'Langue' : 'Language'}
            onClick={() => setLanguageOpen((open) => !open)}>
            <img src={current.flag} alt="" onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = current.fallback;
            }} />
            <small>{current.code}</small>
          </button>
          {languageOpen && (
            <div className="language-popover">
              {(Object.entries(LANGUAGES) as [MenuLanguage, typeof LANGUAGES[MenuLanguage]][])
                .map(([value, option]) => (
                  <button key={value} className={language === value ? 'active' : ''}
                    onClick={() => { onLanguage(value); setLanguageOpen(false); }}>
                    <img src={option.flag} alt="" onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = option.fallback;
                    }} />
                    <strong>{option.label}</strong>
                    {language === value && <i>✓</i>}
                  </button>
                ))}
            </div>
          )}
        </div>
        {accountButton}
        <button aria-label={es ? 'Abrir configuración' : fr ? 'Ouvrir les paramètres' : 'Open settings'}
          title={es ? 'Configuración' : fr ? 'Paramètres' : 'Settings'} onClick={onSettings}
          className="settings-trigger">
          <img src="/assets/icons/settings.svg" alt="" aria-hidden="true" />
        </button>
      </nav>
    </header>
  );
}
